"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get BaseReviewReportWithoutContent () {
        return BaseReviewReportWithoutContent;
    },
    get ReviewReport () {
        return ReviewReport;
    },
    get ReviewReportEasyAddWithoutContent () {
        return ReviewReportEasyAddWithoutContent;
    },
    get ReviewReportExternalAuthor () {
        return ReviewReportExternalAuthor;
    },
    get ReviewReportExternalWithoutContent () {
        return ReviewReportExternalWithoutContent;
    },
    get ReviewReportSendBackContext () {
        return ReviewReportSendBackContext;
    },
    get ReviewReportStandardWithoutContent () {
        return ReviewReportStandardWithoutContent;
    },
    get ReviewReportWithDefault () {
        return ReviewReportWithDefault;
    },
    get ReviewReportWithoutContent () {
        return ReviewReportWithoutContent;
    },
    get ReviewReportWithoutContentWithDefault () {
        return ReviewReportWithoutContentWithDefault;
    }
});
const _zod = require("zod");
const _models = require("@performyard/models");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const _helpers = require("./helpers");
const ReviewReportExternalAuthor = _zod.z.object({
    /**
	 * Author's name
	 */ name: _models.ReviewReportModel.shape.external_author.unwrap().nullable(),
    /**
	 * Email recipient of invite.
	 * Only required field for invite/authoring.
	 * When filled, this review report is "assigned".
	 * This cannot be changed if sentAt is set.
	 */ email: _models.ReviewReportModel.shape.external_author_email.unwrap().nullable(),
    /**
	 * Message to include in the email.
	 */ message: _models.ReviewReportModel.shape.external_author_invitation.unwrap().nullable(),
    /**
	 * null or last sent Date
	 */ sentAt: _models.ReviewReportModel.shape.last_invite.unwrap().nullable()
});
const BaseReviewReportWithoutContent = _zod.z.object({
    _id: _utilsisomorphic.transformObjectId,
    authorTurn: _models.ReviewReportModel.shape.author_turn.unwrap(),
    /**
	 * Authors will be an array containing ObjectIds or nulls in some cases.
	 * When a report is redacted and the user should not see real authors, entries will be "redacted".
	 */ authors: _zod.z.array(_zod.z.union([
        _utilsisomorphic.transformObjectId.nullable(),
        _zod.z.literal("redacted")
    ])),
    authorsDueDate: _models.ReviewReportModel.shape.authors_due_date,
    closed: _models.ReviewReportModel.shape.closed.unwrap().nullable(),
    company: _models.ReviewReportModel.shape.company,
    completeDate: _models.ReviewReportModel.shape.complete_date.unwrap().nullable(),
    completed: _models.ReviewReportModel.shape.completed,
    currentAuthor: _zod.z.union([
        _models.ReviewReportModel.shape.author.unwrap(),
        _zod.z.literal("redacted")
    ]),
    currentSigner: _models.ReviewReportModel.shape.signer.unwrap(),
    /**
	 * The _id of the review_form document this review report is on, not the base_id
	 */ reviewFormId: _models.ReviewReportModel.shape.form_id,
    lastAuthorChange: _models.ReviewReportModel.shape.last_author_change,
    lastSignerChange: _models.ReviewReportModel.shape.last_signer_change,
    lastSignOffComment: _models.ReviewReportModel.shape.sign_off_comment,
    lastSignOffDate: _models.ReviewReportModel.shape.sign_off_date,
    lastSubmitDate: _models.ReviewReportModel.shape.submit_date,
    participants: _zod.z.array(_zod.z.union([
        _utilsisomorphic.transformObjectId.nullable(),
        _zod.z.literal("redacted")
    ])),
    redacted: _models.ReviewReportModel.shape.redacted,
    reviewParent: _models.ReviewReportModel.shape.review_parent,
    signOffDates: _models.ReviewReportModel.shape.sign_off_dates,
    signed: _models.ReviewReportModel.shape.signed,
    signerTurn: _models.ReviewReportModel.shape.signer_turn,
    signers: _models.ReviewReportModel.shape.signers,
    signersDueDate: _models.ReviewReportModel.shape.signers_due_date,
    startDate: _models.ReviewReportModel.shape.start_date,
    status: _models.ReviewReportModel.shape.status.unwrap(),
    subject: _models.ReviewReportModel.shape.subject,
    submit: _models.ReviewReportModel.shape.submit,
    submitDates: _models.ReviewReportModel.shape.submit_dates,
    visible: _models.ReviewReportModel.shape.visible
});
const ReviewReportStandardWithoutContent = BaseReviewReportWithoutContent.extend({
    reviewReportType: _zod.z.literal("standard")
});
const ReviewReportEasyAddWithoutContent = BaseReviewReportWithoutContent.extend({
    /**
	 * Defaults to "approved" if approval is not required.
	 */ approvalStatus: _models.ReviewReportEasyAddApprovalStatus,
    addedBy: _models.ReviewReportModel.shape.easy_add_data.unwrap().shape.created_by.nullable(),
    approvedBy: _models.ReviewReportModel.shape.approval.unwrap().shape.approved_by.nullable(),
    easyAddComment: _models.ReviewReportModel.shape.form_comment,
    easyAddConfigId: _models.ReviewReportModel.shape.easy_add_data.unwrap().shape.easy_add_config,
    reviewReportType: _zod.z.literal("easy-add")
});
const ReviewReportExternalWithoutContent = BaseReviewReportWithoutContent.omit({
    authors: true,
    currentAuthor: true
}).extend({
    authors: _zod.z.array(ReviewReportExternalAuthor).max(1),
    currentAuthor: ReviewReportExternalAuthor,
    reviewReportType: _zod.z.literal("external")
});
const ReviewReportWithoutContent = _zod.z.discriminatedUnion("reviewReportType", [
    ReviewReportStandardWithoutContent,
    ReviewReportEasyAddWithoutContent,
    ReviewReportExternalWithoutContent
]);
const ReviewReportWithoutContentWithDefault = _zod.z.preprocess(_helpers.convertModelToServiceContract, ReviewReportWithoutContent);
/**
 * Content is separate because it is omitted in some responses.
 */ const reviewReportContent = _zod.z.object({
    answers: (0, _utilsisomorphic.toCamelCaseSchema)(_models.ReviewReportModel.shape.answers),
    signOffComments: _models.ReviewReportModel.shape.sign_off_comments
});
const ReviewReport = ReviewReportWithoutContent.and(reviewReportContent);
const ReviewReportWithDefault = _zod.z.preprocess(_helpers.convertModelToServiceContract, ReviewReport);
const ReviewReportSendBackContext = _zod.z.object({
    previousActor: _zod.z.union([
        _utilsisomorphic.transformObjectId,
        _zod.z.literal("redacted")
    ]),
    newActor: _zod.z.union([
        _utilsisomorphic.transformObjectId,
        _zod.z.literal("redacted")
    ]),
    comment: _models.ReviewReportSendBackCommentModel
});
